import { Button, Form } from "../lib";
import { RateInput } from "./rate-input";
import { CityRate, Rate, UserRatesUpdateReq } from "../services";
import { useCallback, useState } from "react";
import { useInit } from "../services/epox/utils";

type Props = {
  config: CityRate[];
  discounts: Record<string, number>;
  loading?: boolean;
  onSubmit: (req: UserRatesUpdateReq) => Promise<void>;
  rates: Rate[];
};

export function RatesForm({
  config,
  discounts,
  loading,
  onSubmit,
  rates,
}: Props) {
  // State
  const [durations, setDurations] = useState<number[]>([]);
  const [amountByDuration, setAmountByDuration] = useState<
    Record<string, number>
  >({});
  const [shotsByDuration, setShotsByDuration] = useState<
    Record<string, number>
  >({});

  // Handlers
  const handleToggle = useCallback(
    (duration: string, active: boolean) => {
      const newDurations = durations.filter(
        (_duration) => _duration !== parseInt(duration)
      );

      if (active) {
        newDurations.push(parseInt(duration));
      }

      setDurations(newDurations);
    },
    [durations]
  );

  const handleChangeAmount = useCallback(
    (duration: string, value: number | null) => {
      const newAmountByDuration = { ...amountByDuration };

      newAmountByDuration[`${duration}`] = value ?? 0;

      setAmountByDuration(newAmountByDuration);
    },
    [amountByDuration]
  );

  const handleChangeShots = useCallback(
    (duration: string, value: number | null) => {
      const newShotsById = { ...shotsByDuration };

      if (value !== null) {
        newShotsById[duration] = value;
      }

      setShotsByDuration(newShotsById);
    },
    [shotsByDuration]
  );

  const handleSubmit = useCallback(async () => {
    const rates = durations.map((duration) => {
      return {
        duration,
        amount: amountByDuration[`${duration}`] ?? 0,
        shots: shotsByDuration[`${duration}`] ?? 0,
      };
    });

    await onSubmit({ rates });
  }, [amountByDuration, durations, onSubmit, shotsByDuration]);

  // Effects
  useInit(() => {
    const initialDurations: number[] = [];
    const initialAmountsByDuration: Record<string, number> = {};
    const initialShotsByDuration: Record<string, number> = {};

    config.forEach(({ duration, amount_min: amountMin, shots: shotsMin }) => {
      const rate = rates.find((r) => r.duration === duration);

      if (rate) {
        initialDurations.push(duration);

        initialAmountsByDuration[`${duration}`] = rate.amount;
        initialShotsByDuration[`${duration}`] = rate.shots;
      } else {
        initialAmountsByDuration[`${duration}`] = amountMin;
        initialShotsByDuration[`${duration}`] = shotsMin;
      }
    });

    setDurations(initialDurations);
    setAmountByDuration(initialAmountsByDuration);
    setShotsByDuration(initialShotsByDuration);
  });

  // Render
  return (
    <Form>
      {config.map(({ duration }) => {
        const rateAmount = amountByDuration[`${duration}`] ?? 0;

        const discountAmount = discounts[`${duration}`] ?? 0;
        const discountedAmount = rateAmount - discountAmount;

        return (
          <RateInput
            key={`${duration}`}
            active={durations.includes(duration)}
            duration={duration}
            amount={rateAmount > 0 ? rateAmount : null}
            discountedAmount={discountedAmount > 0 ? discountedAmount : null}
            shots={shotsByDuration[`${duration}`]}
            onChange={handleToggle}
            onChangeAmount={handleChangeAmount}
            onChangeShots={handleChangeShots}
          />
        );
      })}

      <Button em onPress={handleSubmit} loading={loading}>
        Save changes
      </Button>
    </Form>
  );
}
